import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BackIcon } from 'assets/icons';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import ServiceAreaForm from './components/ServiceAreaForm';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { SERVICE_AREA_COLORS, ServiceAreaStatus, ServiceAreaType } from './constants';
import {
  useCreateServiceAreaMutation,
  useGetServiceAreaByIdQuery,
  useUpdateServiceAreaMutation,
  useUpdateServiceAreaStatusMutation
} from './api';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { If, Loader } from 'components';
import { usePaginatedGetServiceAreasQuery } from './hooks/usePaginatedGetServiceAreasQuery';

interface ShapeData {
  type: ServiceAreaType;
  data: [number, number][] | { centroid: [number, number]; range: number };
}

interface FormErrors {
  name?: string;
  shape?: string;
}

const AddEditServiceArea: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditMode = location.pathname.includes('edit');
  const currentTeam = useAppSelector(selectCurrentTeam);

  // Form state
  const [name, setName] = useState('');
  const [color, setColor] = useState(SERVICE_AREA_COLORS[0]);
  const [selectedMembers, setSelectedMembers] = useState<AssignableMember[]>([]);
  const [shapeData, setShapeData] = useState<ShapeData | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch all enabled service areas
  const { list: existingServiceAreas, isLoading: isLoadingExistingAreas } = usePaginatedGetServiceAreasQuery(null, {
    status: ServiceAreaStatus.ENABLED
  });

  // Transform existing service areas into the format expected by the map, excluding current area in edit mode
  const existingShapes = useMemo(
    () =>
      existingServiceAreas
        ?.filter(area => !isEditMode || area.id !== id)
        .map(area => ({
          type: area.type as ServiceAreaType,
          vertices: area.type === ServiceAreaType.POLYGON ? area.vertices : undefined,
          centroid: area.type === ServiceAreaType.CIRCLE ? area.centroid : undefined,
          range: area.type === ServiceAreaType.CIRCLE ? area.range : undefined,
          color: area.colorCode
        })),
    [existingServiceAreas, isEditMode, id]
  );

  const [createServiceArea, { isLoading: isLoadingCreateServiceArea }] = useCreateServiceAreaMutation();
  const [updateServiceArea, { isLoading: isLoadingUpdateServiceArea }] = useUpdateServiceAreaMutation();
  const [updateServiceAreaStatus, { isLoading: isLoadingUpdateServiceAreaStatus }] =
    useUpdateServiceAreaStatusMutation();

  // Fetch service area data in edit mode
  const { data: serviceAreaData, isLoading: isLoadingServiceArea } = useGetServiceAreaByIdQuery(
    { teamId: currentTeam?.id?.toString() || '', serviceAreaId: id || '' },
    { skip: !isEditMode || !id || !currentTeam?.id }
  );

  // Update form state when service area data is fetched
  useEffect(() => {
    if (serviceAreaData && isEditMode) {
      setName(serviceAreaData.name);
      setColor(serviceAreaData.colorCode);
      setSelectedMembers(
        serviceAreaData?.assignees?.map(assignee => ({
          merchantId: assignee.id,
          name: assignee.name,
          profilePictureUrl: assignee.profilePictureURL,
          email: assignee.email,
          phone: assignee.phone,
          colorCode: serviceAreaData.colorCode,
          id: assignee.id
        }))
      );

      // Set shape data based on service area type
      if (serviceAreaData.type === ServiceAreaType.POLYGON) {
        handleShapeComplete({
          type: ServiceAreaType.POLYGON,
          data: serviceAreaData.vertices
        });
      } else if (serviceAreaData.type === ServiceAreaType.CIRCLE && serviceAreaData.centroid) {
        handleShapeComplete({
          type: ServiceAreaType.CIRCLE,
          data: {
            centroid: serviceAreaData.centroid,
            range: serviceAreaData.range
          }
        });
      }
    }
  }, [serviceAreaData, isEditMode]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleStatusToggle = async () => {
    try {
      await updateServiceAreaStatus({
        teamId: currentTeam.id.toString(),
        serviceAreaId: id || '',
        payload: {
          status:
            serviceAreaData?.status === ServiceAreaStatus.ENABLED
              ? ServiceAreaStatus.DISABLED
              : ServiceAreaStatus.ENABLED
        }
      }).unwrap();
      navigate(-1);
    } catch (error) {
      // Error handling is already handled by the API mutation
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setErrors(prev => ({ ...prev, name: undefined }));
  };

  const handleShapeComplete = (data: ShapeData | null) => {
    setShapeData(data);
    setErrors(prev => ({ ...prev, shape: undefined }));
  };

  const handleSave = async () => {
    setErrors({});

    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Please enter a name for the service area';
    }

    if (
      !shapeData ||
      (shapeData.type === ServiceAreaType.POLYGON && (shapeData?.data as [number, number][]).length === 1)
    ) {
      newErrors.shape = 'Please draw a service area on the map';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const payload = {
        teamId: currentTeam.id.toString(),
        name: name.trim(),
        colorCode: color,
        type: shapeData.type,
        assignees: selectedMembers?.map(member => member.merchantId) || [],
        ...(shapeData.type === ServiceAreaType.POLYGON
          ? { vertices: shapeData.data as [number, number][] }
          : {
              centroid: (shapeData.data as { centroid: [number, number]; range: number }).centroid,
              range: (shapeData.data as { centroid: [number, number]; range: number }).range
            })
      };

      if (isEditMode && id) {
        await updateServiceArea({
          teamId: currentTeam.id.toString(),
          serviceAreaId: id,
          payload: {
            name: name.trim(),
            colorCode: color,
            type: shapeData.type,
            ...(shapeData.type === ServiceAreaType.POLYGON
              ? { vertices: shapeData.data as [number, number][] }
              : {
                  centroid: (shapeData.data as { centroid: [number, number]; range: number }).centroid,
                  range: (shapeData.data as { centroid: [number, number]; range: number }).range
                }),
            assignees: selectedMembers?.map(member => member.merchantId) || []
          }
        }).unwrap();
      } else {
        await createServiceArea(payload).unwrap();
      }
      navigate(-1);
    } catch (error) {
      //error
    }
  };

  if (isLoadingServiceArea || isLoadingExistingAreas) {
    return <Loader />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-10 flex w-full border-b border-borderGray bg-white">
        <div className="flex h-[72px] w-[262px] items-center border-borderGray px-6">
          <BackIcon className="mr-5 cursor-pointer" onClick={handleBackClick} />
          <span className="text-xl font-bold text-primaryText">
            {isEditMode ? 'Edit service area' : 'Add service area'}
          </span>
        </div>
        <div className="flex h-[72px] flex-1 items-center justify-end px-[30px]">
          <If condition={isEditMode}>
            <CustomButton
              id="disable-button"
              type={ButtonType.GHOST}
              childrenContainerStyles={`text-[17px] font-semibold ${
                serviceAreaData?.status === ServiceAreaStatus.ENABLED ? 'text-error' : 'text-successHighlight'
              }`}
              className="mr-4 h-10 border border-borderGray"
              isLoading={isLoadingUpdateServiceAreaStatus}
              onClick={handleStatusToggle}>
              {serviceAreaData?.status === ServiceAreaStatus.ENABLED ? 'Disable' : 'Enable'} service area
            </CustomButton>
          </If>
          <CustomButton
            id="save-button"
            type={ButtonType.PRIMARY}
            childrenContainerStyles="text-[17px] font-semibold"
            className="h-10"
            isLoading={isLoadingCreateServiceArea || isLoadingUpdateServiceArea}
            onClick={handleSave}>
            {isEditMode ? 'Save' : 'Add service area'}
          </CustomButton>
        </div>
      </div>

      <div className="flex h-[calc(100vh-72px)] overflow-hidden">
        <div className="h-full w-full flex-1">
          <div className="flex h-full w-full justify-center">
            <div className="w-full space-y-12 p-6 pb-24 text-headingGray 2xl:w-4/5">
              <ServiceAreaForm
                name={name}
                color={color}
                selectedMembers={selectedMembers}
                onNameChange={handleNameChange}
                onColorChange={setColor}
                onMembersChange={setSelectedMembers}
                onShapeComplete={handleShapeComplete}
                isLoading={isLoadingServiceArea}
                initialShapeData={
                  serviceAreaData
                    ? {
                        type: serviceAreaData.type as ServiceAreaType,
                        vertices: serviceAreaData.vertices || undefined,
                        centroid: serviceAreaData.centroid || undefined,
                        range: serviceAreaData.range || undefined
                      }
                    : undefined
                }
                errors={errors}
                existingShapes={existingShapes}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditServiceArea;
